import { useEffect, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import TableColumn from '../../components/TableColumn/TableColumn';
import api from '../../services/api';
import { toast } from 'react-toastify';

// Mirrors the BP wizard's role catalog. Pick a role, the wizard
// pre-fills permissions; the admin can still drop down to a custom
// permissions set if they need something off-recipe.
const ROLES = [
  { id: 'manager',  title: 'VIP Manager', description: 'Runs the night — events, guestlists, bottle service, ticket sales.', permissions: ['guestlist', 'bottleService', 'ticketSales'] },
  { id: 'host',     title: 'VIP Host',    description: 'Manages door guestlists. Can check in guests.',                       permissions: ['guestlist'] },
  { id: 'promoter', title: 'Promoter',    description: 'Brings guests via tracking link.',                                    permissions: ['guestlist'] },
  { id: 'dj',       title: 'Verified DJ', description: 'Brings their crowd via guestlist. No table or ticket access.',         permissions: ['guestlist'] },
  { id: 'custom',   title: 'Custom',      description: 'Pick the permissions yourself.',                                      permissions: [] },
];

const PERMISSION_OPTIONS = [
  { id: 'guestlist',     label: 'Guest Lists' },
  { id: 'bottleService', label: 'Bottle Service' },
  { id: 'ticketSales',   label: 'Ticket Sales' },
];

const ALL_SIX = ['guestlist', 'bottleService', 'ticketSales', 'offers', 'withdrawalRequests', 'followers'];
const PERM_LABEL = {
  guestlist: 'Guest Lists',
  bottleService: 'Bottle Service',
  ticketSales: 'Tickets',
  offers: 'Promotions',
  withdrawalRequests: 'Withdrawals',
  followers: 'Followers',
};

function formatLastActive(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

function lastActiveColor(value) {
  if (!value) return 'rgba(255, 255, 255, 0.35)';
  const diffDays = Math.floor((Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return '#2ECC71';
  if (diffDays < 30) return '#F1C40F';
  return '#E74C3C';
}

function formatPermissions(perms) {
  if (!perms || perms.length === 0) return 'Full access';
  if (perms.includes('all')) return 'Full access';
  if (ALL_SIX.every(p => perms.includes(p))) return 'Full access';
  return perms.map(p => PERM_LABEL[p] || p).join(', ');
}

function Organizers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Add-organizer wizard state (mirrors BP's moderator.tsx).
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState('role'); // 'role' | 'email' | 'venues' | 'confirm'
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [lookupResult, setLookupResult] = useState(null); // { id, name, email }
  const [lookupTried, setLookupTried] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [allVenues, setAllVenues] = useState([]);
  const [selectedVenueIds, setSelectedVenueIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const loadList = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get('/organizer-requests/all', {
        params: q ? { search: q } : undefined,
      });
      // Flatten: one row per (request × venue). Same shape as MyOrganizer.
      const flattened = (data?.data || []).flatMap(r =>
        (r.venues || []).map(v => ({
          User: r.user?.name || '—',
          Email: r.user?.email || '',
          Venue: v.name,
          Role: r.role || formatPermissions(r.permissions),
          Added: r.created_at ? new Date(r.created_at).toLocaleDateString() : '—',
          'Last Sign In': (
            <span style={{ color: lastActiveColor(r.user?.last_active), fontWeight: 500 }}>
              {formatLastActive(r.user?.last_active)}
            </span>
          ),
          _id: `${r._id}-${v.id}`,
          _requestId: r._id,
          _venueId: v.id,
          _userId: r.user?.id,
          _userName: r.user?.name || '',
          _venueName: v.name,
        }))
      ).sort((a, b) => {
        if (a.User !== b.User) return a.User.localeCompare(b.User);
        return a.Venue.localeCompare(b.Venue);
      });
      setRows(flattened);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load organizers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    loadList(searchInput.trim());
  };

  const openWizard = async () => {
    setShowWizard(true);
    setWizardStep('role');
    setSelectedRole(null);
    setPermissions([]);
    setEmailInput('');
    setLookupResult(null);
    setLookupTried(false);
    setSelectedVenueIds(new Set());
    if (allVenues.length === 0) {
      try {
        const { data } = await api.get('/venues', { params: { records: 9999 } });
        const list = (data?.data || data || [])
          .map(v => ({ id: v._id || v.id, name: v.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setAllVenues(list);
      } catch (err) {
        console.error('Failed to load venues:', err);
        toast.error('Could not load venues.');
      }
    }
  };

  const pickRole = (role) => {
    setSelectedRole(role.id);
    setPermissions(role.permissions);
    setWizardStep('email');
  };

  const lookupByEmail = async (e) => {
    e?.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    setLookupTried(true);
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const { data } = await api.get('/organizer-requests/fetch/prospective-organizers', {
        params: { email_exact: email },
      });
      const match = (data?.data || [])[0];
      if (match) {
        setLookupResult({
          id: match._id,
          name: `${match.firstName || ''} ${match.lastName || ''}`.trim() || match.email || 'Unnamed',
          email: match.email,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLookupLoading(false);
    }
  };

  const submitWizard = async () => {
    if (!lookupResult) return;
    // Verified DJ is venue-independent — they pick venues themselves when
    // creating a guestlist. Every other role needs at least one venue.
    if (selectedRole !== 'dj' && selectedVenueIds.size === 0) return;
    setSubmitting(true);
    try {
      await api.post('/organizer-requests/add-organizer', {
        selectedUserId: lookupResult.id,
        permissions: permissions,
        venues: Array.from(selectedVenueIds),
        role: ROLES.find(r => r.id === selectedRole)?.title,
      });
      toast.success(`Added ${lookupResult.name} as ${selectedRole === 'dj' ? 'a Verified DJ' : 'organizer'}.`);
      setShowWizard(false);
      await loadList(search);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to add organizer.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await api.delete(`/organizer-requests/my-roles/${removeTarget.requestId}/venue/${removeTarget.venueId}`);
      toast.success(`Removed ${removeTarget.userName} from ${removeTarget.venueName}.`);
      setRemoveTarget(null);
      await loadList(search);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to remove.');
    } finally {
      setRemoving(false);
    }
  };

  const tableData = rows.map(r => ({
    ...r,
    Remove: (
      <button
        type="button"
        title="Remove this user from this venue"
        onClick={() =>
          setRemoveTarget({
            requestId: r._requestId,
            venueId: r._venueId,
            userName: r._userName,
            venueName: r._venueName,
          })
        }
        style={{
          background: 'rgba(225, 29, 72, 0.15)',
          border: '1px solid rgba(225, 29, 72, 0.35)',
          borderRadius: 6,
          padding: '6px 10px',
          cursor: 'pointer',
          color: '#FF5C7A',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <MdDelete size={16} />
      </button>
    ),
  }));

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article>
          <section className="moderators">
            <div style={{ marginBottom: 16 }}>
              <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 8px 0' }}>Organizers</h1>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, margin: 0 }}>
                Every user with an active organizer assignment on Zona. Search by name, email, or venue.
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <form onSubmit={onSearch} style={{ flex: 1, display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Search by name, email, or venue…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                color: '#fff',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              style={{
                padding: '10px 18px',
                background: 'rgba(79, 48, 234, 0.2)',
                border: '1px solid rgba(79, 48, 234, 0.5)',
                borderRadius: 8,
                color: '#A89AFF',
                cursor: 'pointer',
              }}
            >
              Search
            </button>
          </form>
          <button
            type="button"
            onClick={openWizard}
            style={{
              padding: '10px 18px',
              background: 'linear-gradient(135deg, #0A3FD6, #7229F5)',
              border: 0,
              borderRadius: 8,
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + Add Organizer
          </button>
        </div>

        {loading ? (
          <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>Loading…</div>
        ) : tableData.length === 0 ? (
          <div style={{ color: 'rgba(255,255,255,0.6)', padding: 40, textAlign: 'center' }}>
            {search ? `No organizers match "${search}".` : 'No organizers yet.'}
          </div>
        ) : (
          <TableColumn
            columns={['User', 'Email', 'Venue', 'Role', 'Added', 'Last Sign In', 'Remove']}
            data={tableData}
          />
        )}

      {showWizard && (
        <div
          onClick={() => setShowWizard(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0f0f17', borderRadius: 14, padding: 28, minWidth: 480, maxWidth: 600,
              maxHeight: '80vh', overflowY: 'auto', color: '#fff',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Add Organizer</h3>
              <button onClick={() => setShowWizard(false)} style={{ background: 'transparent', border: 0, color: '#fff', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>

            {wizardStep === 'role' && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0 }}>What role are you assigning?</p>
                {ROLES.map(r => (
                  <button
                    key={r.id}
                    onClick={() => pickRole(r)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: 14, marginBottom: 10, borderRadius: 10,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      cursor: 'pointer', color: '#fff',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{r.title}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{r.description}</div>
                  </button>
                ))}
              </>
            )}

            {wizardStep === 'email' && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0 }}>
                  Enter the exact email of the person to add. They must already have a Zona account.
                </p>
                {selectedRole === 'custom' && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>Permissions</div>
                    {PERMISSION_OPTIONS.map(opt => (
                      <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <input
                          type="checkbox"
                          checked={permissions.includes(opt.id)}
                          onChange={() => {
                            setPermissions(prev =>
                              prev.includes(opt.id)
                                ? prev.filter(p => p !== opt.id)
                                : [...prev, opt.id]
                            );
                          }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                )}
                <form onSubmit={lookupByEmail} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    style={{
                      flex: 1, padding: '10px 14px', background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#fff',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!emailInput.trim() || lookupLoading}
                    style={{
                      padding: '10px 18px', background: 'rgba(79,48,234,0.2)',
                      border: '1px solid rgba(79,48,234,0.5)', borderRadius: 8, color: '#A89AFF', cursor: 'pointer',
                    }}
                  >
                    {lookupLoading ? 'Looking…' : 'Find'}
                  </button>
                </form>
                {lookupResult ? (
                  <div style={{ padding: 14, borderRadius: 10, background: 'rgba(79,48,234,0.10)', border: '1px solid rgba(79,48,234,0.3)', marginBottom: 16 }}>
                    <div style={{ fontWeight: 600 }}>{lookupResult.name}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{lookupResult.email}</div>
                  </div>
                ) : lookupTried ? (
                  <div style={{ color: '#FF5C7A', marginBottom: 16 }}>No Zona user with that email.</div>
                ) : null}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                  <button onClick={() => setWizardStep('role')} style={{ padding: '10px 18px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer' }}>Back</button>
                  <button
                    disabled={!lookupResult || (selectedRole === 'custom' && permissions.length === 0)}
                    onClick={() => setWizardStep(selectedRole === 'dj' ? 'confirm' : 'venues')}
                    style={{
                      padding: '10px 18px',
                      background: 'linear-gradient(135deg, #0A3FD6, #7229F5)',
                      border: 0, borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer',
                      opacity: (!lookupResult || (selectedRole === 'custom' && permissions.length === 0)) ? 0.4 : 1,
                    }}
                  >
                    Next
                  </button>
                </div>
              </>
            )}

            {wizardStep === 'venues' && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0 }}>Pick the venue(s) for {lookupResult?.name}.</p>
                <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 16, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }}>
                  {allVenues.map(v => (
                    <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedVenueIds.has(v.id)}
                        onChange={() => {
                          setSelectedVenueIds(prev => {
                            const next = new Set(prev);
                            if (next.has(v.id)) next.delete(v.id);
                            else next.add(v.id);
                            return next;
                          });
                        }}
                      />
                      <span>{v.name}</span>
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                  <button onClick={() => setWizardStep('email')} style={{ padding: '10px 18px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer' }}>Back</button>
                  <button
                    disabled={selectedVenueIds.size === 0}
                    onClick={() => setWizardStep('confirm')}
                    style={{
                      padding: '10px 18px',
                      background: 'linear-gradient(135deg, #0A3FD6, #7229F5)',
                      border: 0, borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer',
                      opacity: selectedVenueIds.size === 0 ? 0.4 : 1,
                    }}
                  >
                    Next ({selectedVenueIds.size} venue{selectedVenueIds.size === 1 ? '' : 's'})
                  </button>
                </div>
              </>
            )}

            {wizardStep === 'confirm' && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0 }}>Confirm:</p>
                <div style={{ padding: 14, borderRadius: 10, background: 'rgba(79,48,234,0.10)', border: '1px solid rgba(79,48,234,0.3)', marginBottom: 16 }}>
                  <div><strong>{lookupResult?.name}</strong> <span style={{ color: 'rgba(255,255,255,0.6)' }}>· {lookupResult?.email}</span></div>
                  <div style={{ marginTop: 6 }}>Role: <strong>{ROLES.find(r => r.id === selectedRole)?.title}</strong></div>
                  <div style={{ marginTop: 4 }}>Permissions: {formatPermissions(permissions)}</div>
                  <div style={{ marginTop: 4 }}>Venues: {selectedRole === 'dj' ? 'None needed — DJ adds their own' : selectedVenueIds.size}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                  <button onClick={() => setWizardStep(selectedRole === 'dj' ? 'email' : 'venues')} style={{ padding: '10px 18px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer' }}>Back</button>
                  <button
                    disabled={submitting}
                    onClick={submitWizard}
                    style={{
                      padding: '10px 18px',
                      background: 'linear-gradient(135deg, #0A3FD6, #7229F5)',
                      border: 0, borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer',
                      opacity: submitting ? 0.4 : 1,
                    }}
                  >
                    {submitting ? 'Adding…' : (selectedRole === 'dj' ? 'Add DJ' : 'Add Organizer')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {removeTarget && (
        <div
          onClick={() => !removing && setRemoveTarget(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#0f0f17', borderRadius: 14, padding: 28, minWidth: 380, color: '#fff' }}>
            <h3 style={{ marginTop: 0 }}>Remove organizer</h3>
            <p style={{ color: 'rgba(255,255,255,0.7)' }}>
              Remove <strong>{removeTarget.userName}</strong> from <strong>{removeTarget.venueName}</strong>?
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setRemoveTarget(null)} disabled={removing} style={{ padding: '10px 18px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmRemove} disabled={removing} style={{ padding: '10px 18px', background: '#E11D48', border: 0, borderRadius: 8, color: '#fff', cursor: 'pointer' }}>
                {removing ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
          </section>
        </article>
      </div>
    </main>
  );
}

export default Organizers;
